type EventCallback = (data: any) => void;

class WebSocketService {
    private ws: WebSocket | null = null;
    private reconnectAttempts = 0;
    private reconnectTimer?: ReturnType<typeof setTimeout>;
    private wanted = false;
    private reconnectDelay = 3000;
    private listeners: Map<string, Set<EventCallback>> = new Map();
    private connectionStatusListeners: Set<(status: boolean) => void> = new Set();
    private isConnected = false;

    connect() {
        this.wanted = true;
        if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
            console.log('[WebSocket] Ya está conectado');
            return;
        }

        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const backendHost = new URL(process.env.REACT_APP_BACKEND_URL || window.location.origin).host;
        const wsUrl = `${protocol}//${backendHost}/ws/citas/`;

        console.log('[WebSocket] Conectando a:', wsUrl);

        const socket = new WebSocket(wsUrl);
        this.ws = socket;

        this.ws.onopen = () => {
            if (this.ws !== socket) return;
            console.log('[WebSocket] ✅ Conectado exitosamente');
            this.isConnected = true;
            this.reconnectAttempts = 0;
            this.notifyConnectionStatus(true);
        };

        this.ws.onmessage = (event) => {
            if (this.ws !== socket) return;
            try {
                const data = JSON.parse(event.data);
                console.log('[WebSocket] 📨 Mensaje recibido:', data);
                // Separar invalidaciones públicas de los eventos privados de citas.
                this.notifyListeners(data.type === 'espacios_actualizados' ? 'espacios_actualizados' : 'cita_actualizada', data);
            } catch (error) {
                console.error('[WebSocket] Error parseando mensaje', error);
            }
        };

        this.ws.onclose = () => {
            if (this.ws !== socket) return;
            console.log('[WebSocket] 🔌 Desconectado');
            this.isConnected = false;
            this.notifyConnectionStatus(false);
            this.attemptReconnect();
        };

        this.ws.onerror = (error) => {
            console.error('[WebSocket] ❌ Error', error);
            socket.close();
        };
    }

    private attemptReconnect() {
        if (!this.wanted) return;
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = setTimeout(() => {
            console.log(
                `[WebSocket] 🔄 Reconectando intento ${this.reconnectAttempts + 1}`
            );
            if (!this.wanted) return;
            this.reconnectAttempts++;
            this.connect();
        }, Math.min(30000, this.reconnectDelay * Math.max(1, this.reconnectAttempts)));
    }

    disconnect() {
        this.wanted = false;
        clearTimeout(this.reconnectTimer);
        const socket = this.ws;
        this.ws = null;
        socket?.close();
        this.isConnected = false;
        this.notifyConnectionStatus(false);
    }

    on(event: string, callback: EventCallback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event)!.add(callback);
        console.log(`[WebSocket] Listener registrado para evento: ${event}`);
        return () => this.off(event, callback);
    }

    off(event: string, callback: EventCallback) {
        this.listeners.get(event)?.delete(callback);
    }

    private notifyListeners(event: string, data: any) {
        const callbacks = this.listeners.get(event);
        console.log(
            `[WebSocket] Notificando ${callbacks?.size || 0} listeners para evento: ${event}`
        );
        callbacks?.forEach((cb) => cb(data));
    }

    onConnectionStatus(callback: (connected: boolean) => void) {
        this.connectionStatusListeners.add(callback);
        callback(this.isConnected);
        return () => this.connectionStatusListeners.delete(callback);
    }

    private notifyConnectionStatus(connected: boolean) {
        this.connectionStatusListeners.forEach((cb) => cb(connected));
    }

    getConnected(): boolean {
        return this.isConnected;
    }
}

export const websocketService = new WebSocketService();
